/**
 * `lib/sickWording.ts` — #2491. One place for how a sick report is named.
 *
 * A clinical team (KKH) says "MC". Every other team says "Off sick". The
 * signal is the team's own sick category, the one with behaviour `mc`: an
 * abbreviation or label of "MC" marks a clinical team. Teams that rename it
 * (the Harbourline demo uses "SD") read the neutral words. While the
 * catalogue is loading, or when a team has no sick category, the wording
 * stays as it was ("MC"), so a clinical team never flashes the neutral form.
 */
import { useDateRequestCategories } from "../api/useDateRequestCategories";
import { useActiveMeTeamId } from "./useActiveMeTeamId";

interface SickCategoryShape {
  behaviour: string;
  label: string;
  abbreviation?: string | null;
}

/** True when the team's sick category carries the clinical "MC" tag. */
export function isClinicalSick(categories: readonly SickCategoryShape[] | undefined): boolean {
  const sick = (categories ?? []).find((c) => c.behaviour === "mc");
  if (!sick) return true;
  const abbr = (sick.abbreviation ?? "").trim().toUpperCase();
  return abbr === "MC" || /\bMC\b/i.test(sick.label);
}

/** The short tag for a sick report: "MC" or "Off sick". */
export function sickTag(clinical: boolean): string {
  return clinical ? "MC" : "Off sick";
}

/** The long form: "Off sick (MC)" or "Off sick". */
export function sickLong(clinical: boolean): string {
  return clinical ? "Off sick (MC)" : "Off sick";
}

/** Whether the active team is clinical, from its category catalogue. */
export function useSickClinical(teamId: string | undefined): boolean {
  const { data } = useDateRequestCategories(teamId);
  return isClinicalSick(data);
}

/** Same, for the member's active team (calendar and sheets). */
export function useActiveSickClinical(): boolean {
  return useSickClinical(useActiveMeTeamId());
}
